import styled from '@emotion/styled'
import type { ReactNode } from 'react'

import Button from '@segment/matcha/components/Button'
import IconWrapper from '@segment/matcha/components/IconWrapper'
import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'
import { LaunchIcon, CheckIcon, LearningIcon } from '@segment/matcha/components/Icons/Brandbook'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

interface FeatureProps {
  icon: ReactNode
  title: string
  body: string
}

const copy = {
  heading: `How to get started`,
}

const data: FeatureProps[] = [
  {
    icon: <CheckIcon />,
    title: `Apply`,
    body: `Join Twilio Segment by submitting your application. Every application is reviewed by a partner manager. Expect to hear back within 3 weeks. If you’re a fit, we’ll contact you with the agreement and next steps.`,
  },

  {
    icon: <LearningIcon />,
    title: `Learn`,
    body: `Dive deep into the Twilio Segment product by accessing a demo environment and learn more about the #1 CDP for worldwide market share IDC 2022, in our free certification program.`,
  },

  {
    icon: <LaunchIcon />,
    title: `Launch`,
    body: `Augment your existing suite of services with your new Segment Certification and Customer Data Platform knowledge to help your clients fully realize the power of their first-party customer data.`,
  },
]

const applyButton = {
  text: `Join Twilio Segment`,
  url: `https://partners.segment.com/English/register_email.aspx/`,
}

const GetStarted = () => {
  return (
    <SectionBase variant="noCopy">
      <StyledSectionContents>
        <CopyTextBase centered={true}>
          <CopyText.Heading tag="h3">{copy.heading}</CopyText.Heading>
        </CopyTextBase>
        <ColumnTrack columns={1} overrides={{ md: 3 }}>
          {data.map((feature) => (
            <FeatureWrapper key={feature.title} alignment="left" headerPosition="top">
              <FeatureBox.Header>
                <IconWrapper elevation={1} variant="circle">
                  {feature.icon}
                </IconWrapper>
              </FeatureBox.Header>
              <FeatureBox.Body>
                <FeatureBox.Title variant="h4">{feature.title}</FeatureBox.Title>
                <FeatureBox.Text>{feature.body}</FeatureBox.Text>
              </FeatureBox.Body>
            </FeatureWrapper>
          ))}
        </ColumnTrack>
        <BottomInfo>
          <Button size="large" variant="filled" color="primary" tag="a" href={applyButton.url} target="_blank">
            {applyButton.text}
          </Button>
        </BottomInfo>
      </StyledSectionContents>
    </SectionBase>
  )
}

const StyledSectionContents = styled(Section.Contents)`
  max-width: 1120px;
  margin: 0 auto;
  display: grid;
  gap: 40px;

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    gap: 80px;
  }
`

const FeatureWrapper = styled(FeatureBoxBase)`
  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    max-width: 352px;
  }
`

const BottomInfo = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    gap: 40px;
  }
`

export default GetStarted
